import { PropTypes } from 'typed/imports';
import { PureComponent, ReactEventHandler1, ReactEventHandler2, ComponentEventHandler1 } from 'typed/components/utils';
import { Component, ReactNode } from 'react';
import { PeriodType, entities } from 'typed/api';
import { default as FormattedResource, Resource, resourcePropType } from '../FormattedResource/FormattedResource';
import RadioGroup from 'components/common/RadioGroup/RadioGroup';
import TimeStamp from 'components/common/TimeStamp/TimeStamp';
import DayPicker from 'react-day-picker';
import { LocaleUtils } from 'react-day-picker/types/utils';
import withDateLocaleUtils, { dateLocaleUtilsPropTypes } from 'components/common/withDateLocaleUtils/withDateLocaleUtils';
import { Period, PeriodRange } from 'typed/utils';
import 'react-day-picker/lib/style.css';
import './PeriodCalendar.less';


const TIME_PERIOD_TRANSLATION_MAP: { [k: number/*TODO why ain't PeriodType possible?*/]: Resource } =
{
    [PeriodType.DAY]: { id: 'common.period.day' },
    [PeriodType.WEEK]: { id: 'common.period.week' },
    [PeriodType.MONTH]: { id: 'common.period.month' },
    [PeriodType.QUARTER]: { id: 'common.period.quarter' },
    [PeriodType.YEAR]: { id: 'common.period.year' }
};

export { PeriodType };

export const periodTypePropType = PropTypes.oneOfType([
    PropTypes.oneOf(Object.values(PeriodType)),
    PropTypes.shape({
        ID: PropTypes.number.isRequired,
        Description: resourcePropType.isRequired
    })
]);

export interface Props {
    value?: Period | null;
    periodTypes?: (PeriodType | entities.PerformanceIndicatorPeriodType)[];
    minDate?: Date;
    maxDate?: Date;
    disabled?: boolean;
    className?: string;
    onChange?: ComponentEventHandler1<Period>;

    locale?: string;
    localeUtils?: LocaleUtils;
    getDateFormat?: () => string;
};

interface State { visiblePeriod: Period; }

class PeriodCalendar extends PureComponent<Props, State> {
    static readonly PeriodType = PeriodType;

    static readonly periodTypePropType = periodTypePropType;

    static readonly propTypes = {
        value: PropTypes.instanceOf(Period),
        periodTypes: PropTypes.arrayOf(periodTypePropType),
        minDate: PropTypes.instanceOf(Date),
        maxDate: PropTypes.instanceOf(Date),
        disabled: PropTypes.bool,
        onChange: PropTypes.func,

        ...dateLocaleUtilsPropTypes
    };

    static readonly defaultProps = { periodTypes: Object.values(PeriodType).filter(p => typeof p === 'number') };

    state: State = { visiblePeriod: new Period(PeriodType.DAY) };

    componentWillMount() {
        const { value, periodTypes: [mostDetailedPeriodType] = [PeriodType.DAY] } = this.props;
        this.setState({ visiblePeriod: value || new Period(typeof mostDetailedPeriodType === 'number' ? mostDetailedPeriodType : mostDetailedPeriodType.ID) });
    }

    componentDidUpdate() {
        // dirty hacks,
        // DayPicker doesn't have any week modifiers, so to show week selection we have to update classes manually
        // and Popup renders content asynchronously, so we have to wait for it to be done before classes update
        setTimeout(() => {
            let popup, container: Element;
            if (this.state && this.state.visiblePeriod && this.state.visiblePeriod.type === PeriodType.WEEK && this.refs['drop-panel'] && (popup = (this.refs['drop-panel'] as Component).refs['popup']) && (container = (popup as any).container)) {

                Array.from(container.querySelectorAll('.DayPicker-Week--today'))
                    .filter(week => !week.querySelector('.DayPicker-Day--today'))
                    .forEach(untodayWeek => untodayWeek.className = untodayWeek.className.replace(/\s+DayPicker-Week--today/, ''));

                Array.from(container.querySelectorAll('.DayPicker-Week--selected'))
                    .filter(week => !week.querySelector('.DayPicker-Day--selected'))
                    .forEach(unselectedWeek => unselectedWeek.className = unselectedWeek.className.replace(/\s+DayPicker-Week--selected/, ''));

                Array.from(container.querySelectorAll('.DayPicker-Week--disabled'))
                    .filter(week => !week.querySelector('.DayPicker-Day--disabled'))
                    .forEach(undisabledWeek => undisabledWeek.className = undisabledWeek.className.replace(/\s+DayPicker-Week--disabled/, ''));

                Array.from(container.querySelectorAll('.DayPicker-Week'))
                    .filter(week => !week.className.includes('--today') && week.querySelector('.DayPicker-Day--today'))
                    .forEach(retodayWeek => retodayWeek.className += ' DayPicker-Week--today');

                Array.from(container.querySelectorAll('.DayPicker-Week'))
                    .filter(week => !week.className.includes('--selected') && week.querySelector('.DayPicker-Day--selected'))
                    .forEach(reselectedWeek => reselectedWeek.className += ' DayPicker-Week--selected');

                Array.from(container.querySelectorAll('.DayPicker-Week'))
                    .filter(week => !week.className.includes('--disabled') && week.querySelector('.DayPicker-Day--disabled'))
                    .forEach(redisabledWeek => redisabledWeek.className += ' DayPicker-Week--disabled');
            }
        }, 0);
    }

    _onVisiblePeriodTypeChange: ComponentEventHandler1<PeriodType> = (_sender, value) => {
        this.setState({ visiblePeriod: new Period(value, this.state.visiblePeriod) });
    }

    _onVisibleDateChange: ReactEventHandler1<Date> = (value) => {
        this.setState({ visiblePeriod: new Period(this.state.visiblePeriod.type, value) });
    }

    _onTickClick: ReactEventHandler1<Date> = (date) => {
        const result = new Period(this.state.visiblePeriod.type, date);
        if (this.props.onChange &&
            (!this.props.minDate || result >= this.props.minDate) &&
            (!this.props.maxDate || result <= this.props.maxDate)
        ) {
            this.props.onChange(this, result);
        }
    }

    _onWeekClick: ReactEventHandler2<number, Date[]> = (_weekNumber, days, _event) => {
        this._onTickClick(days[0], _event);
    }

    _onDayClick: ReactEventHandler2<Date, unknown> = (date, _, event) => {
        this._onTickClick(date, event);
    }

    render() {
        const
            { value, periodTypes, minDate, maxDate, disabled } = this.props,
            { visiblePeriod } = this.state;

        let visibleRange: PeriodRange | null = null, visibleRangeDescription: ReactNode;
        if (visiblePeriod) {
            switch (visiblePeriod ? visiblePeriod.type : null) {
                case PeriodType.YEAR:
                    visibleRange = new PeriodRange(visiblePeriod.type, visiblePeriod, 5, 6);
                    visibleRangeDescription = (
                        <span>
                            <TimeStamp detalizationPeriodType={PeriodType.YEAR} exactDatePeriodType={null} value={visibleRange.first()} />
                            &nbsp;&ndash;&nbsp;
                        <TimeStamp detalizationPeriodType={PeriodType.YEAR} exactDatePeriodType={null} value={visibleRange.last()} />
                        </span>
                    );
                    break;

                case PeriodType.QUARTER:
                    visibleRange = new PeriodRange(visiblePeriod.type, visiblePeriod, 4 + visiblePeriod.getUTCQuarter() - 1, 8 - visiblePeriod.getUTCQuarter());
                    visibleRangeDescription = (
                        <span>
                            <TimeStamp detalizationPeriodType={PeriodType.YEAR} exactDatePeriodType={null} value={visibleRange.first()} />
                            &nbsp;&ndash;&nbsp;
                        <TimeStamp detalizationPeriodType={PeriodType.YEAR} exactDatePeriodType={null} value={visibleRange.last()} />
                        </span>
                    );
                    break;

                case PeriodType.MONTH:
                    visibleRange = new PeriodRange(visiblePeriod.type, visiblePeriod, visiblePeriod.getUTCMonth(), 12 - visiblePeriod.getUTCMonth() - 1);
                    visibleRangeDescription = <TimeStamp detalizationPeriodType={PeriodType.YEAR} exactDatePeriodType={null} value={visiblePeriod} />;
                    break;

                case PeriodType.WEEK:
                case PeriodType.DAY:
                    visibleRangeDescription = <TimeStamp detalizationPeriodType={PeriodType.MONTH} exactDatePeriodType={null} value={visiblePeriod} />;
                    break;
            }
        }

        return (
            <div className="period-calendar">
                {
                    visiblePeriod && periodTypes!.length > 1 ?
                        <RadioGroup
                            className="control-group"
                            layout={RadioGroup.Layout.HORIZONTAL}
                            appearance={RadioGroup.Appearance.BUTTON}
                            disabled={disabled}
                            onChange={this._onVisiblePeriodTypeChange}
                            value={visiblePeriod.type}>
                            {periodTypes!.map(pt =>
                                typeof pt === 'number' ?
                                    <RadioGroup.Option key={pt} value={pt}>
                                        <FormattedResource resource={TIME_PERIOD_TRANSLATION_MAP[pt]} />
                                    </RadioGroup.Option> :
                                    <RadioGroup.Option key={pt.ID} value={pt.ID}>
                                        <FormattedResource resource={pt.Description} />
                                    </RadioGroup.Option>
                            )}
                        </RadioGroup> :
                        null
                }
                {
                    visiblePeriod ?
                        <div className="range">
                            <button className="inline fa fa-chevron-left" disabled={disabled} onClick={this._onVisibleDateChange.bind(this, visibleRange ? visiblePeriod.add(-visibleRange.length) : new Period(PeriodType.MONTH, visiblePeriod).add(-1))} />
                            <button className="inline fa fa-chevron-right" disabled={disabled} onClick={this._onVisibleDateChange.bind(this, visibleRange ? visiblePeriod.add(visibleRange.length) : new Period(PeriodType.MONTH, visiblePeriod).add(1))} />
                            <div>{visibleRangeDescription}</div>
                        </div> :
                        null
                }
                {
                    visiblePeriod ?
                        visiblePeriod.type !== PeriodType.DAY && visiblePeriod.type !== PeriodType.WEEK ?
                            <div className="ticks">
                                {
                                    visibleRange!
                                        .groupBy((_, index) => (index / 4) | 0)
                                        .map(group =>
                                            <div key={group.key} className="group">
                                                {
                                                    visiblePeriod.type === PeriodType.QUARTER ?
                                                        <div className="name">{group[0].getFullYear()}</div> :
                                                        null
                                                }
                                                {group.map(tick =>
                                                    <div key={+tick}
                                                        className={
                                                            'tick ' +
                                                            (value && value.type === visiblePeriod.type && +tick === +value ? ' active' : '') +
                                                            (+tick === +new Period(visiblePeriod.type) ? ' current' : '') +
                                                            (minDate && tick < new Period(visiblePeriod.type, minDate) || maxDate && tick > new Period(visiblePeriod.type, maxDate) ? ' disabled' : '')
                                                        }
                                                        onClick={this._onTickClick.bind(this, tick)}>
                                                        <TimeStamp detalizationPeriodType={visiblePeriod.type} exactDatePeriodType={null} short value={new Date(tick)} />
                                                    </div>
                                                )}
                                            </div>
                                        )
                                }
                            </div> :
                            <DayPicker
                                className={visiblePeriod.type === PeriodType.DAY ? 'day' : 'week'}
                                locale={this.props.locale}
                                localeUtils={this.props.localeUtils}
                                canChangeMonth={false}
                                showWeekNumbers
                                firstDayOfWeek={1}
                                month={visiblePeriod}
                                disabledDays={(d: Date) => !!minDate && d < new Period(visiblePeriod.type, minDate) || !!maxDate && d > new Period(visiblePeriod.type, maxDate)}
                                selectedDays={(d: Date) => !!value && value.type === visiblePeriod.type && d >= value.start && d <= value.end}
                                onWeekClick={visiblePeriod.type === PeriodType.WEEK ? this._onWeekClick : () => { /*DayPicker doesn't check for handler nullity and fails if handler is not passed */ }}
                                onDayClick={this._onDayClick} /> :
                        null
                }
            </div>
        );
    }
}

export default withDateLocaleUtils(PeriodCalendar) as typeof PeriodCalendar;